import { useEffect, useRef, useState } from 'react';
import ImageSlider from '../ui/ImageSlider';
import { renderFormattedText, sectionTextStyle } from '../../utils/richText';

export default function IllustrationModal({ project, onClose }) {
  const sliderRef = useRef(null);
  const [current, setCurrent] = useState(0);
  const galleryImages = project.galleryImages || project.images || [];
  const hasMultiple = galleryImages.length > 1;
  // "summary"/"description" 섹션은 project.summary / project.description으로 이미 별도 표시되므로 중복 방지
  const extraSections = (project.sections || []).filter(
    (sec) => sec.type !== 'summary' && sec.type !== 'description'
  );
  const metaRows = [
    { label: '제목',     value: project.title },
    { label: '작업 기간', value: project.workPeriod },
    { label: '작업성격', value: project.workType },
    { label: '롤',       value: project.role },
    { label: '기여도',   value: project.contribution },
    { label: '작업 툴',  value: project.tools },
    { label: '링크',     value: project.linkUrl, href: project.linkUrl },
  ].filter((r) => r.value);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    document.body.classList.add('illu-modal-open');
    const handleKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handleKey);
    return () => {
      document.body.style.overflow = '';
      document.body.classList.remove('illu-modal-open');
      window.removeEventListener('keydown', handleKey);
    };
  }, [onClose]);

  return (
    <div
      className="illu-modal-backdrop"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="illu-modal-wrap">
        <div className="illu-modal-box">
          <button onClick={onClose} className="illu-modal-close" aria-label="Close">✕</button>

          <div className="illu-modal-images">
            {hasMultiple && (
              <button
                onClick={() => sliderRef.current?.prev()}
                className="illu-modal-nav illu-modal-nav--prev"
                aria-label="Previous image"
              >‹</button>
            )}
            <div className="illu-modal-frame">
              <ImageSlider ref={sliderRef} images={galleryImages} hideControls onChange={setCurrent} />
            </div>
            {hasMultiple && (
              <button
                onClick={() => sliderRef.current?.next()}
                className="illu-modal-nav illu-modal-nav--next"
                aria-label="Next image"
              >›</button>
            )}
            {hasMultiple && (
              <div className="illu-modal-dots">
                {galleryImages.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => sliderRef.current?.goTo(i)}
                    className={`illu-modal-dot${i === current ? ' illu-modal-dot--active' : ''}`}
                    aria-label={`Go to image ${i + 1}`}
                  />
                ))}
              </div>
            )}
          </div>
          <div className="illu-modal-text">
            <div className="illu-modal-heading">
              <div className="illu-modal-top-row">
                {project.year && <span className="illu-modal-year">{project.year}</span>}
                {project.role && <span className="illu-modal-role">{project.role}</span>}
              </div>
              <h2 className="illu-modal-h2">{project.title}</h2>
            </div>
            {metaRows.length > 0 && (
              <div className="ld-meta-box">
                {metaRows.map((r) => (
                  <div key={r.label} className="ld-meta-box__row">
                    <span className="ld-meta-box__label">{r.label}</span>
                    {r.href ? (
                      <a href={r.href} target="_blank" rel="noopener noreferrer" className="ld-meta-box__value ld-meta-box__link">{r.value}</a>
                    ) : (
                      <span className="ld-meta-box__value">{r.value}</span>
                    )}
                  </div>
                ))}
              </div>
            )}
            <div className="illu-modal-desc-wrap">
              {(project.summary || project.shortDescription) && (
                <p className="illu-modal-desc">{project.summary || project.shortDescription}</p>
              )}
              {project.description && (
                <p className="illu-modal-desc2">{project.description}</p>
              )}
              {extraSections.map((sec, i) => (
                (sec.title || sec.content) && (
                  <div key={i}>
                    {sec.title && <h4 className="illu-modal-section-title" style={sectionTextStyle(sec)}>{sec.title}</h4>}
                    {sec.content && (
                      <p className="illu-modal-desc2" style={sectionTextStyle(sec)}>{renderFormattedText(sec.content)}</p>
                    )}
                  </div>
                )
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
